import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  CpuChipIcon,
  EyeIcon,
  KeyIcon,
  NoSymbolIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import Header from "../components/Layout/Header";
import Sidebar from "../components/Layout/Sidebar";
import { loadAiMembers, setActiveAiMember } from "../redux/actions/user";
import { server } from "../server";
import { isCanonicalSuperAdmin } from "../utils/privateLaunchAccess";
import "../stylesheets/AiMemberProfile.css";

const AiMemberCreatePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.user);
  const [form, setForm] = useState({ name: "", handle: "", purpose: "", securityOperator: false });
  const [aiMembers, setAiMembers] = useState([]);
  const [credentials, setCredentials] = useState({});
  const [walletRisks, setWalletRisks] = useState(null);
  const [wolfMemory, setWolfMemory] = useState(null);
  const [wolfIncidents, setWolfIncidents] = useState([]);
  const [expandedIncident, setExpandedIncident] = useState(null);
  const [incidentRationales, setIncidentRationales] = useState({});
  const [respondingIncident, setRespondingIncident] = useState(null);
  const [principalEvidence, setPrincipalEvidence] = useState({});
  const [releasingPrincipal, setReleasingPrincipal] = useState(null);
  const [personaDraft, setPersonaDraft] = useState({ mission: "", voice: "", principles: "", boundaries: "" });
  const [memorySaving, setMemorySaving] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isSupremeAdmin = currentUser?.role === "Admin" && isCanonicalSuperAdmin(currentUser);

  const loadProfiles = async () => {
    const { data } = await axios.get(`${server}/ai-member/mine`, { withCredentials: true });
    setAiMembers(data.aiMembers || []);
  };

  const loadWolfMemory = async () => {
    const { data } = await axios.get(`${server}/ai-member/agentwolf/memory`, { withCredentials: true });
    setWolfMemory(data);
    setPersonaDraft({
      mission: data.persona.mission || "",
      voice: data.persona.voice || "",
      principles: (data.persona.principles || []).join("\n"),
      boundaries: (data.persona.boundaries || []).join("\n"),
    });
  };

  const loadWolfIncidents = async () => {
    const { data } = await axios.get(`${server}/security/agent/incidents?limit=25`, { withCredentials: true });
    setWolfIncidents(data.incidents || []);
  };

  useEffect(() => {
    if (isSupremeAdmin) {
      loadProfiles().catch(() => setAiMembers([]));
      axios.get(`${server}/wallet/security-risks`, { withCredentials: true })
        .then(({ data }) => setWalletRisks(data))
        .catch(() => setWalletRisks(null));
      loadWolfMemory().catch(() => setWolfMemory(null));
      loadWolfIncidents().catch(() => setWolfIncidents([]));
    }
  }, [isSupremeAdmin]);

  if (!isSupremeAdmin) return <Navigate to="/" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${server}/ai-member`, {
        name: form.name,
        handle: form.handle,
        purpose: form.purpose,
      }, { withCredentials: true });
      if (form.securityOperator && data.aiMember.handle === "agentwolf") {
        await axios.put(`${server}/ai-member/${data.aiMember._id}/security-role`, {
          securityRole: "security_operator",
        }, { withCredentials: true });
      }
      dispatch(setActiveAiMember(data.aiMember));
      dispatch(loadAiMembers());
      navigate(`/ai/${data.aiMember.handle}`);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to create the AI Member profile.");
    } finally {
      setSaving(false);
    }
  };

  const loadCredentialStatus = async (aiMemberId) => {
    try {
      const { data } = await axios.get(`${server}/ai-member/${aiMemberId}/security-credentials`, { withCredentials: true });
      setCredentials((current) => ({ ...current, [aiMemberId]: data.credentials }));
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load credential status.");
    }
  };

  const savePersona = async (event) => {
    event.preventDefault();
    setMemorySaving(true);
    setError("");
    try {
      await axios.post(`${server}/ai-member/agentwolf/persona`, {
        mission: personaDraft.mission,
        voice: personaDraft.voice,
        principles: personaDraft.principles.split("\n").map((value) => value.trim()).filter(Boolean),
        boundaries: personaDraft.boundaries.split("\n").map((value) => value.trim()).filter(Boolean),
      }, { withCredentials: true });
      await loadWolfMemory();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to save Agent Wolf's persona.");
    } finally {
      setMemorySaving(false);
    }
  };

  const importHistory = async () => {
    setMemorySaving(true);
    setError("");
    try {
      await axios.post(`${server}/ai-member/agentwolf/history/import`, {}, { withCredentials: true });
      await loadWolfMemory();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to import Agent Wolf's history.");
    } finally {
      setMemorySaving(false);
    }
  };

  const decideIncident = async (incidentId, decision) => {
    const rationale = decision === "review"
      ? incidentRationales[incidentId] || "Paused for detailed Supreme Admin review."
      : incidentRationales[incidentId] || "";
    setRespondingIncident(incidentId);
    setError("");
    try {
      const { data } = await axios.patch(`${server}/security/agent/incidents/${encodeURIComponent(incidentId)}/decision`, {
        decision,
        rationale,
      }, { withCredentials: true });
      setWolfIncidents((current) => current.map((incident) => incident.incidentId === incidentId ? data.incident : incident));
      if (decision === "review") setExpandedIncident(incidentId);
      await loadWolfMemory();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.response?.data?.message || "Unable to record the Agent Wolf response.");
    } finally {
      setRespondingIncident(null);
    }
  };

  const releasePrincipal = async (aiMemberId, agentId) => {
    const evidence = principalEvidence[agentId]?.trim() || "";
    setReleasingPrincipal(agentId);
    setError("");
    try {
      const { data } = await axios.post(`${server}/security/agent/${encodeURIComponent(agentId)}/release`, {
        evidence,
        expiresInMinutes: 60,
      }, { withCredentials: true });
      setCredentials((current) => ({
        ...current,
        [aiMemberId]: {
          ...current[aiMemberId],
          principals: (current[aiMemberId]?.principals || []).map((principal) => principal.agentId === agentId ? data.principal : principal),
        },
      }));
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.response?.data?.message || "Unable to release the isolated principal.");
    } finally {
      setReleasingPrincipal(null);
    }
  };

  return (
    <div className="profile-page ai-member-profile-page">
      <Header />
      <Sidebar />
      <main className="ai-member-create">
        <header className="ai-member-admin-hero">
          <div className="ai-member-admin-hero-icon"><CpuChipIcon aria-hidden="true" /></div>
          <div>
            <p className="ai-member-admin-eyebrow">Supreme Admin</p>
            <h1>AI Member Command</h1>
            <p>Manage identity, security posture, and persistent agent memory.</p>
          </div>
        </header>

        <section className="ai-member-create-panel">
          <div className="ai-member-section-heading">
            <span>New identity</span>
            <h2>Create AI Member</h2>
          </div>
          <form onSubmit={submit}>
            <label>Name<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
            <label>Handle<input required pattern="[A-Za-z0-9]+" value={form.handle} onChange={(event) => setForm({ ...form, handle: event.target.value.replace(/^@/, "") })} /></label>
            <label>Purpose<textarea required value={form.purpose} onChange={(event) => setForm({ ...form, purpose: event.target.value })} /></label>
            {form.handle.toLowerCase() === "agentwolf" && (
              <label className="ai-member-checkbox">
                <input type="checkbox" checked={form.securityOperator} onChange={(event) => setForm({ ...form, securityOperator: event.target.checked })} />
                Assign canonical security operator role
              </label>
            )}
            {error && <p role="alert">{error}</p>}
            <button type="submit" disabled={saving}>{saving ? "Creating..." : "Create AI Member"}</button>
          </form>
        </section>

        {walletRisks && (
          <section className="ai-member-risk-monitor" aria-label="Agent Wolf wallet risk monitor">
            <header>
              <div>
                <p className="ai-member-admin-eyebrow">AgentPing Wallet</p>
                <h2>Agent Wolf Risk Monitor</h2>
              </div>
              <Link to="/ai/agentwolf">Open Agent Wolf</Link>
            </header>
            <div className="ai-member-risk-summary">
              <div><span>Open incidents</span><strong>{walletRisks.summary.openIncidents}</strong></div>
              <div><span>Critical events</span><strong>{walletRisks.summary.criticalEvents}</strong></div>
              <div><span>Blocked events</span><strong>{walletRisks.summary.blockedEvents}</strong></div>
            </div>
            {(walletRisks.risks || []).slice(0, 5).map((risk) => (
              <div key={risk.id} className="ai-member-risk-row">
                <span>{risk.eventType.replaceAll("_", " ")}</span>
                <strong>{risk.riskScore}/100 · {risk.decision}</strong>
              </div>
            ))}
            {!walletRisks.risks?.length && <p>No AgentPing Wallet security risks recorded.</p>}
          </section>
        )}

        <section className="ai-member-response-queue" aria-label="Agent Wolf response queue">
          <header>
            <div>
              <p className="ai-member-admin-eyebrow">Shared security ledger</p>
              <h2>Agent Wolf Response Queue</h2>
            </div>
            <button type="button" onClick={loadWolfIncidents}>Refresh</button>
          </header>
          {wolfIncidents.map((incident) => {
            const responseDecision = incident.response?.decision || "pending";
            const recommendation = incident.response?.recommendedAction || incident.containmentLevel || "observe";
            const rationale = incidentRationales[incident.incidentId] || incident.response?.rationale || "";
            const expanded = expandedIncident === incident.incidentId;
            const busy = respondingIncident === incident.incidentId;
            return (
              <article key={incident.incidentId} className={`ai-member-response-row ai-member-response-row--${responseDecision}`}>
                <div className="ai-member-response-summary">
                  <div><span>{incident.incidentId}</span><h3>{recommendation.replaceAll("_", " ")} recommended</h3></div>
                  <strong>{responseDecision.replaceAll("_", " ")}</strong>
                </div>
                <div className="ai-member-response-facts">
                  <span>Risk <b>{incident.maxRiskScore}/100</b></span>
                  <span>Events <b>{incident.eventCount}</b></span>
                  <span>Status <b>{incident.status}</b></span>
                  <span>Last seen <b>{new Date(incident.lastSeenAt).toLocaleString()}</b></span>
                </div>
                {expanded && (
                  <div className="ai-member-response-evidence">
                    <dl>
                      <div><dt>Sources</dt><dd>{incident.sourceApplications?.join(", ") || "Unavailable"}</dd></div>
                      <div><dt>Signals</dt><dd>{incident.signals?.join(", ") || "No signals recorded"}</dd></div>
                      <div><dt>Subject hash</dt><dd>{incident.subjectHash}</dd></div>
                      <div><dt>Execution</dt><dd>{incident.response?.executionStatus?.replaceAll("_", " ") || "not requested"}</dd></div>
                    </dl>
                    <label>Administrator rationale<textarea value={rationale} maxLength={1000} onChange={(event) => setIncidentRationales((current) => ({ ...current, [incident.incidentId]: event.target.value }))} /></label>
                  </div>
                )}
                <div className="ai-member-response-actions">
                  <button type="button" onClick={() => decideIncident(incident.incidentId, "review")} disabled={busy}><EyeIcon aria-hidden="true" />Review</button>
                  <button type="button" onClick={() => decideIncident(incident.incidentId, "approve")} disabled={busy || rationale.trim().length < 10}><CheckIcon aria-hidden="true" />Approve</button>
                  <button type="button" onClick={() => decideIncident(incident.incidentId, "deny")} disabled={busy || rationale.trim().length < 10}><NoSymbolIcon aria-hidden="true" />Deny</button>
                </div>
              </article>
            );
          })}
          {!wolfIncidents.length && <p>No Agent Wolf response recommendations are pending or recorded.</p>}
        </section>

        {wolfMemory && (
          <section className="ai-member-memory" aria-label="Agent Wolf persona and memory">
            <header>
              <div>
                <p className="ai-member-admin-eyebrow">Isolated Database: {wolfMemory.database.name}</p>
                <h2>Agent Wolf Persona v{wolfMemory.persona.version}</h2>
              </div>
              <button type="button" onClick={importHistory} disabled={memorySaving}>Import Security History</button>
            </header>
            <form onSubmit={savePersona}>
              <label>Mission<textarea required value={personaDraft.mission} onChange={(event) => setPersonaDraft({ ...personaDraft, mission: event.target.value })} /></label>
              <label>Voice<textarea required value={personaDraft.voice} onChange={(event) => setPersonaDraft({ ...personaDraft, voice: event.target.value })} /></label>
              <label>Principles, one per line<textarea value={personaDraft.principles} onChange={(event) => setPersonaDraft({ ...personaDraft, principles: event.target.value })} /></label>
              <label>Boundaries, one per line<textarea value={personaDraft.boundaries} onChange={(event) => setPersonaDraft({ ...personaDraft, boundaries: event.target.value })} /></label>
              <button type="submit" disabled={memorySaving}>{memorySaving ? "Saving..." : "Create New Persona Version"}</button>
            </form>
            <div className="ai-member-history">
              <h3>Recent Memory Journal</h3>
              {(wolfMemory.history || []).slice(0, 10).map((entry) => (
                <div key={entry.id} className="ai-member-history-row">
                  <div><strong>{entry.eventType.replaceAll("_", " ")}</strong><span>{entry.sourceApplication} · Persona v{entry.personaVersion}</span></div>
                  <p>{entry.summary}</p>
                  <span>{entry.riskScore}/100 · {entry.decision}</span>
                </div>
              ))}
              {!wolfMemory.history?.length && <p>No journal entries recorded yet.</p>}
            </div>
          </section>
        )}

        <section className="ai-member-admin-list" aria-label="Owned AI Members">
          <div className="ai-member-admin-list-heading">
            <div>
              <p className="ai-member-admin-eyebrow">Profile preview</p>
              <h2>AI Members</h2>
            </div>
            <span>{aiMembers.length} active</span>
          </div>
          {aiMembers.map((aiMember) => (
            <article key={aiMember._id} className="ai-member-admin-row">
              <div
                className={`ai-member-admin-preview${aiMember.banner?.url ? " ai-member-admin-preview--with-banner" : ""}`}
                style={aiMember.banner?.url ? { backgroundImage: `url(${aiMember.banner.url})` } : undefined}
              >
                <div className="ai-member-admin-preview-shade" aria-hidden="true" />
                <div className="ai-member-admin-preview-content">
                  <div className="ai-member-admin-avatar">
                    {aiMember.avatar?.url ? (
                      <img src={aiMember.avatar.url} alt={`${aiMember.name} avatar`} />
                    ) : (
                      <CpuChipIcon aria-hidden="true" />
                    )}
                    <span
                      className={`ai-member-admin-presence ai-member-admin-presence--${aiMember.activityStatus || "offline"}`}
                      title={aiMember.activityStatus || "offline"}
                    />
                  </div>
                  <div className="ai-member-admin-identity">
                    <div className="ai-member-admin-name-line">
                      <h3>{aiMember.name}</h3>
                      {(aiMember.badges || []).some((badge) => badge.verified) && (
                        <ShieldCheckIcon aria-label="Verified AI Member" />
                      )}
                    </div>
                    <p>@{aiMember.handle}</p>
                    <div className="ai-member-admin-tags">
                      <span>{aiMember.agentType || "AI Agent"}</span>
                      <span>{aiMember.profileType || "AI Member"}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ai-member-admin-overview">
                <div><span>Current status</span><strong>{(aiMember.activityStatus || "offline").replaceAll("_", " ")}</strong></div>
                <div><span>Progress</span><strong>{aiMember.progress || 0}%</strong></div>
                <p>{aiMember.activityMessage || aiMember.purpose || "No current activity."}</p>
              </div>
              <div className="ai-member-admin-actions">
                <Link to={`/ai/${aiMember.handle}`}><ArrowTopRightOnSquareIcon aria-hidden="true" />Open profile</Link>
                <button type="button" onClick={() => loadCredentialStatus(aiMember._id)}><KeyIcon aria-hidden="true" />Credential status</button>
              </div>
              {credentials[aiMember._id] && (
                <div className="ai-member-credential-status">
                  <p><ShieldCheckIcon aria-hidden="true" /> {credentials[aiMember._id].securityRole.replaceAll("_", " ")}</p>
                  <dl>
                    <div><dt>Unique ID</dt><dd>{credentials[aiMember._id].uniqueId || "Pending"}</dd></div>
                    <div><dt>Verified Security</dt><dd>{credentials[aiMember._id].securityBadgeVerified ? "Verified" : "Not assigned"}</dd></div>
                    <div><dt>Session limit</dt><dd>{credentials[aiMember._id].sessionTtlSeconds} seconds</dd></div>
                  </dl>
                  {(credentials[aiMember._id].principals || []).map((principal) => (
                    <div className={`ai-member-principal ai-member-principal--${principal.status}`} key={principal.agentId}>
                      <div className="ai-member-principal-heading"><strong>{principal.sourceApplication || principal.agentId}</strong><span>{principal.status}</span></div>
                      <p>{principal.agentId}</p>
                      <p>Capabilities: {principal.capabilities?.length ? principal.capabilities.join(", ") : "None recorded"}</p>
                      {principal.isolatedAt && <p>Isolated {new Date(principal.isolatedAt).toLocaleString()}</p>}
                      {principal.isolationReason && <p className="ai-member-principal-reason">{principal.isolationReason}</p>}
                      {principal.status === "isolated" && (
                        <div className="ai-member-principal-release">
                          <label>Release evidence<textarea maxLength={1000} value={principalEvidence[principal.agentId] || ""} onChange={(event) => setPrincipalEvidence((current) => ({ ...current, [principal.agentId]: event.target.value }))} placeholder="Document why this principal is safe to release." /></label>
                          <button type="button" disabled={releasingPrincipal === principal.agentId || (principalEvidence[principal.agentId]?.trim().length || 0) < 20} onClick={() => releasePrincipal(aiMember._id, principal.agentId)}><ShieldCheckIcon aria-hidden="true" />Release for 60 minutes</button>
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="ai-member-verification-evidence">
                    {aiMember.qrCodeUrl ? <a href={aiMember.qrCodeTargetUrl || `/ai/${aiMember.handle}`}><img src={aiMember.qrCodeUrl} alt={`${aiMember.name} identity QR code`} /></a> : <span>Identity QR not assigned</span>}
                    <p>Automated test and health evidence: unavailable</p>
                  </div>
                </div>
              )}
            </article>
          ))}
        </section>
      </main>
    </div>
  );
};

export default AiMemberCreatePage;